import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { CheckInEvent, EventGuest, EventGuestLookup, EventTicketIssuance } from '@/api/types';
import { checkedGuest } from '@/features/events/checkIn';
import { adminClient } from '@/lib/adminClient';

// Event check-in (LIM-4707): a guest at the door, the tickets they are
// handed, and the event's tally.

export const eventsKeys = {
  all: ['events'] as const,
  event: (code: string) => [...eventsKeys.all, code] as const,
  seen: (code: string) => [...eventsKeys.event(code), 'seen'] as const,
  issuances: (code: string) => [...eventsKeys.event(code), 'issuances'] as const,
  tally: (code: string) => [...eventsKeys.issuances(code), 'tally'] as const,
  issuanceList: (code: string) => [...eventsKeys.issuances(code), 'list'] as const,
};

/** The tally and the list move with every phone at the door, not only this one. */
const ISSUANCES_REFRESH_MS = 30_000;

/** The server's ceiling for one page of issuances (partner_service's pagination option). */
const ISSUANCES_PAGE_SIZE = 1000;

/** The event code in the URL, /events/:code/…. */
export function useEventCode(): string {
  const { code } = useParams();
  if (code === undefined) throw new Error('An event page is routed without an event code');
  return code;
}

/**
 * The event as the last guest read carried it. No route reads an event on
 * its own, so the header learns the event's name and window from the first
 * guest the door reads, and has neither before then. Never fetched: the
 * guest reads below write it.
 */
export function useSeenEvent(code: string) {
  return useQuery<CheckInEvent>({ queryKey: eventsKeys.seen(code), queryFn: skipToken });
}

export interface EventTally {
  readonly guests: number;
  readonly tickets: number;
}

/** How many guests have been handed tickets, and how many tickets. Refreshed every 30 s. */
export function useEventTally(code: string) {
  return useQuery({
    // One issuance per page: the totals come with every page.
    queryKey: eventsKeys.tally(code),
    queryFn: () => adminClient.listEventTicketIssuances(code, { pageSize: 1 }),
    // The gateway omits a zero, so an absent total is none yet.
    select: (page): EventTally => ({ guests: page.totalSize ?? 0, tickets: page.totalTicketCount ?? 0 }),
    staleTime: 0,
    refetchInterval: ISSUANCES_REFRESH_MS,
  });
}

export interface EventIssuances {
  readonly issuances: readonly EventTicketIssuance[];
  readonly tally: EventTally;
}

/** Every guest handed tickets, most recent first, over every page. Refreshed every 30 s. */
export function useEventTicketIssuances(code: string) {
  return useQuery({
    queryKey: eventsKeys.issuanceList(code),
    queryFn: async (): Promise<EventIssuances> => {
      const issuances: EventTicketIssuance[] = [];
      let pageToken: string | undefined;
      let tally: EventTally;
      do {
        const page = await adminClient.listEventTicketIssuances(code, { pageSize: ISSUANCES_PAGE_SIZE, pageToken });
        issuances.push(...(page.eventTicketIssuances ?? []));
        tally = { guests: page.totalSize ?? 0, tickets: page.totalTicketCount ?? 0 };
        pageToken = page.nextPageToken || undefined;
      } while (pageToken !== undefined);
      return { issuances, tally };
    },
    staleTime: 0,
    refetchInterval: ISSUANCES_REFRESH_MS,
  });
}

/**
 * Reads a guest by a scan or a search. A mutation, not a query: each scan is
 * a fresh read of whoever is in front of the camera, and a mutate's own
 * callbacks fire only for the latest call, so a slow answer for the previous
 * guest never replaces the current one.
 */
export function useResolveEventGuest(code: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (lookup: EventGuestLookup) => checkedGuest(await adminClient.resolveEventGuest(code, lookup)),
    onSuccess: (guest: EventGuest) => queryClient.setQueryData(eventsKeys.seen(code), guest.event),
  });
}

/**
 * Hands the guest their tickets. Whatever came of it, the tally is read
 * again: a refusal can mean another phone handed them over.
 */
export function useIssueEventTickets(code: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => checkedGuest(await adminClient.issueEventTickets(code, userId)),
    onSuccess: (guest: EventGuest) => queryClient.setQueryData(eventsKeys.seen(code), guest.event),
    onSettled: () => queryClient.invalidateQueries({ queryKey: eventsKeys.issuances(code) }),
  });
}
